import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import {
    FileText, Shield, FileSignature, Upload, Save, Eye, History, X, Download, Search,
    RefreshCw, CheckCircle2, AlertCircle, Ban, ChevronRight,
} from "lucide-react";
import { fmtDate } from "../pages/crm/shared";

const DOC_META = {
    TOS: { title: "Termini di Servizio", icon: FileText },
    PRIVACY_VD: { title: "Privacy Policy VendiDirect", icon: Shield },
    DPA: { title: "Accordo Trattamento Dati (DPA)", icon: FileSignature },
};
const STATUS_META = {
    current: { label: "Versione corrente", cls: "bg-green-900/30 text-green-300 border-green-700/40", icon: CheckCircle2 },
    outdated: { label: "Versione precedente", cls: "bg-yellow-900/30 text-yellow-200 border-yellow-700/40", icon: AlertCircle },
    never: { label: "Mai accettato", cls: "bg-red-900/30 text-red-300 border-red-700/40", icon: Ban },
};

export default function AdminLegalDocs() {
    const [docs, setDocs] = useState(null);
    const [selected, setSelected] = useState(null); // doc_type when editor is open
    const [acceptanceDoc, setAcceptanceDoc] = useState(null);
    const [loadError, setLoadError] = useState("");

    const load = useCallback(async () => {
        setLoadError("");
        try {
            const r = await api.get("/admin/legal/documents");
            setDocs(r.data.items || []);
        } catch (e) {
            setDocs([]);
            setLoadError(e?.response?.data?.detail || "Impossibile caricare i documenti legali. Verifica i permessi o riprova.");
        }
    }, []);
    useEffect(() => { load(); }, [load]);

    if (!docs && !loadError) return <div className="text-ink-400 text-sm py-12 text-center">Caricamento documenti legali...</div>;

    return (
        <div data-testid="admin-legal" className="space-y-6">
            <div>
                <div className="text-[10px] uppercase tracking-wider text-gold flex items-center gap-2">
                    <FileSignature size={13} /> Documenti Legali
                </div>
                <h2 className="font-serif text-2xl text-ink-50 mt-1">TOS · Privacy · DPA</h2>
                <p className="text-ink-400 text-xs mt-1">Pubblica nuove versioni e consulta lo stato di accettazione di ogni agenzia sulla piattaforma.</p>
            </div>

            {loadError && (
                <div data-testid="admin-legal-load-error" className="border border-red-700/40 bg-red-900/20 text-red-200 text-sm p-3 flex items-center justify-between gap-3">
                    <span>{loadError}</span>
                    <button onClick={load} className="cta-outline px-3 py-1 text-[11px]">Riprova</button>
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {docs.map((d) => {
                    const meta = DOC_META[d.doc_type] || { title: d.label, icon: FileText };
                    const Icon = meta.icon;
                    const total = d.counts.total_agencies || 0;
                    const pctCurrent = total ? Math.round(d.counts.accepted_current * 100 / total) : 0;
                    return (
                        <div key={d.doc_type} data-testid={`admin-legal-card-${d.doc_type}`} className="border border-white/10 bg-ink-900/40 p-5 space-y-3">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2 text-gold text-[10px] uppercase tracking-wider">
                                    <Icon size={13} /> {meta.title}
                                </div>
                                <div className="text-[10px] uppercase tracking-wider text-ink-400">v{d.version?.replace(/^v/, "")}</div>
                            </div>
                            {d.is_placeholder && <div className="text-[10px] text-yellow-300 border border-yellow-700/40 bg-yellow-900/20 px-2 py-1 inline-block">Placeholder</div>}
                            <div className="text-[11px] text-ink-500">
                                Pubblicato: {d.published_at ? fmtDate(d.published_at) : "—"}
                                {d.updated_by_email ? ` · da ${d.updated_by_email}` : ""}
                            </div>
                            <div>
                                <div className="text-[10px] uppercase tracking-wider text-ink-400 mb-1 flex justify-between">
                                    <span>Adesione corrente</span>
                                    <span>{d.counts.accepted_current}/{total} ({pctCurrent}%)</span>
                                </div>
                                <div className="h-1.5 bg-ink-950">
                                    <div className={`h-full ${pctCurrent === 100 ? "bg-green-400" : "bg-gold"}`} style={{ width: `${pctCurrent}%` }} />
                                </div>
                                <div className="grid grid-cols-3 gap-1 mt-2 text-[9px] text-center">
                                    <div className="border border-white/10 py-1 text-green-300">✓ {d.counts.accepted_current}</div>
                                    <div className="border border-white/10 py-1 text-yellow-300">⚠ {d.counts.accepted_outdated}</div>
                                    <div className="border border-white/10 py-1 text-red-300">✕ {d.counts.never_accepted}</div>
                                </div>
                            </div>
                            <div className="flex items-center gap-2 pt-2">
                                <button
                                    data-testid={`admin-legal-manage-${d.doc_type}`}
                                    onClick={() => setSelected(d.doc_type)}
                                    className="cta-outline text-[11px] px-3 py-1.5 flex items-center gap-1.5"
                                >
                                    <Upload size={11} /> Nuova versione
                                </button>
                                <button
                                    data-testid={`admin-legal-view-acceptances-${d.doc_type}`}
                                    onClick={() => setAcceptanceDoc(d.doc_type)}
                                    className="cta-gold text-[11px] px-3 py-1.5 flex items-center gap-1.5"
                                >
                                    Accettazioni <ChevronRight size={11} />
                                </button>
                            </div>
                        </div>
                    );
                })}
            </div>

            {selected && (
                <DocEditor
                    docType={selected}
                    onClose={() => setSelected(null)}
                    onSaved={() => { setSelected(null); load(); }}
                />
            )}
            {acceptanceDoc && (
                <AcceptancesView
                    docType={acceptanceDoc}
                    onClose={() => setAcceptanceDoc(null)}
                />
            )}
        </div>
    );
}

// ============================================================
// Doc Editor: publish new version (text or PDF) + view history
// ============================================================
function DocEditor({ docType, onClose, onSaved }) {
    const meta = DOC_META[docType];
    const Icon = meta.icon;
    const [current, setCurrent] = useState(null);
    const [versions, setVersions] = useState([]);
    const [mode, setMode] = useState("text");
    const [text, setText] = useState("");
    const [saving, setSaving] = useState(false);
    const [msg, setMsg] = useState("");
    const [openVersion, setOpenVersion] = useState(null);
    const fileRef = useRef(null);

    const load = useCallback(async () => {
        try {
            const r = await api.get(`/admin/legal/documents/${docType}`);
            setCurrent(r.data.current || {});
            setVersions(r.data.versions || []);
            setText(r.data.current?.content_text || "");
        } catch { /* silent */ }
    }, [docType]);
    useEffect(() => { load(); }, [load]);

    const publishText = async () => {
        if (!text.trim()) { setMsg("Testo vuoto"); return; }
        if (!window.confirm(`Pubblicare nuova versione ${meta.title}? Tutte le agenzie dovranno riaccettare al prossimo accesso.`)) return;
        setSaving(true); setMsg("");
        try {
            await api.put(`/admin/legal/documents/${docType}/text`, { content_text: text });
            setMsg("Nuova versione pubblicata.");
            await load();
            onSaved();
        } catch (e) {
            setMsg(e?.response?.data?.detail || "Errore pubblicazione");
        } finally { setSaving(false); }
    };

    const uploadPdf = async (file) => {
        if (!file) return;
        if (!file.name.toLowerCase().endsWith(".pdf")) { setMsg("Serve un PDF"); return; }
        if (!window.confirm(`Pubblicare ${meta.title} come PDF? Tutte le agenzie dovranno riaccettare.`)) return;
        setSaving(true); setMsg("");
        try {
            const fd = new FormData();
            fd.append("file", file);
            await api.post(`/admin/legal/documents/${docType}/pdf`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            setMsg("PDF pubblicato.");
            await load();
            onSaved();
        } catch (e) {
            setMsg(e?.response?.data?.detail || "Errore upload");
        } finally { setSaving(false); if (fileRef.current) fileRef.current.value = ""; }
    };

    return (
        <div data-testid={`admin-legal-editor-${docType}`} className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" onClick={onClose}>
            <div className="relative w-full max-w-4xl max-h-[90vh] border border-gold/30 bg-ink-950 flex flex-col" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between border-b border-white/10 px-6 py-4">
                    <div>
                        <div className="font-serif text-lg text-ink-50 flex items-center gap-2">
                            <Icon size={16} className="text-gold" /> {meta.title}
                        </div>
                        <div className="text-[10px] uppercase tracking-wider text-ink-400 mt-1">
                            Versione corrente: {current?.version || "—"} · {current?.format === "pdf" ? "PDF" : "Testo"}
                        </div>
                    </div>
                    <button onClick={onClose} data-testid={`admin-legal-editor-close-${docType}`} className="text-ink-400 hover:text-ink-50" aria-label="Chiudi"><X size={16} /></button>
                </div>
                <div className="flex-1 overflow-y-auto p-6 space-y-4">
                    <div className="flex gap-2 border border-white/15 w-fit">
                        <button
                            data-testid={`admin-legal-mode-text-${docType}`}
                            onClick={() => setMode("text")}
                            className={`px-3 py-1.5 text-[11px] uppercase tracking-wider ${mode === "text" ? "bg-gold text-ink-950" : "text-ink-300"}`}
                        >Testo</button>
                        <button
                            data-testid={`admin-legal-mode-pdf-${docType}`}
                            onClick={() => setMode("pdf")}
                            className={`px-3 py-1.5 text-[11px] uppercase tracking-wider ${mode === "pdf" ? "bg-gold text-ink-950" : "text-ink-300"}`}
                        >PDF</button>
                    </div>

                    {mode === "text" ? (
                        <>
                            <textarea
                                data-testid={`admin-legal-textarea-${docType}`}
                                rows={16}
                                value={text}
                                onChange={(e) => setText(e.target.value)}
                                placeholder={`Testo del documento ${meta.title}...`}
                                className="w-full bg-ink-950 border border-white/15 px-4 py-3 text-sm text-ink-100 focus:border-gold outline-none font-mono"
                            />
                            <div className="text-[10px] text-ink-500">{text.length.toLocaleString("it-IT")} caratteri</div>
                            <div className="flex justify-end gap-2">
                                {current?.format === "text" && current?.content_text !== text && (
                                    <span className="text-[11px] text-yellow-300 self-center">Modifiche non salvate</span>
                                )}
                                <button
                                    onClick={publishText}
                                    disabled={saving}
                                    data-testid={`admin-legal-publish-text-${docType}`}
                                    className="cta-gold px-5 py-2 text-xs flex items-center gap-2"
                                >
                                    <Save size={12} /> {saving ? "Pubblicazione..." : "Pubblica nuova versione"}
                                </button>
                            </div>
                        </>
                    ) : (
                        <>
                            <input
                                ref={fileRef}
                                type="file"
                                accept=".pdf,application/pdf"
                                data-testid={`admin-legal-pdf-input-${docType}`}
                                onChange={(e) => uploadPdf(e.target.files?.[0])}
                                className="hidden"
                            />
                            <button
                                onClick={() => fileRef.current?.click()}
                                disabled={saving}
                                data-testid={`admin-legal-pdf-btn-${docType}`}
                                className="w-full border border-dashed border-white/20 hover:border-gold hover:bg-white/5 py-8 text-sm text-ink-200 flex items-center justify-center gap-2 transition-colors"
                            >
                                <Upload size={16} /> {saving ? "Upload in corso..." : "Seleziona PDF (max 15MB)"}
                            </button>
                            {current?.format === "pdf" && current?.pdf_filename && (
                                <div className="flex items-center gap-2 text-[12px] text-ink-300 bg-ink-900/40 border border-white/10 px-3 py-2">
                                    <FileText size={13} className="text-gold" />
                                    PDF attivo: <span className="text-ink-100">{current?.pdf_filename}</span>
                                    <a
                                        href={`${process.env.REACT_APP_BACKEND_URL}/api/legal/documents/${docType}/pdf`}
                                        target="_blank" rel="noreferrer"
                                        className="ml-auto cta-outline text-[10px] px-2 py-1 flex items-center gap-1"
                                    >
                                        <Eye size={11} /> Apri
                                    </a>
                                </div>
                            )}
                        </>
                    )}

                    {msg && (
                        <div className={`text-xs p-3 border ${msg.toLowerCase().includes("errore") || msg.toLowerCase().includes("vuoto") || msg.toLowerCase().includes("serve") ? "border-red-700/40 bg-red-900/20 text-red-300" : "border-gold/40 bg-gold/5 text-gold"}`}>
                            {msg}
                        </div>
                    )}

                    <div className="border-t border-white/10 pt-4">
                        <div className="text-[10px] uppercase tracking-wider text-gold flex items-center gap-2 mb-3">
                            <History size={13} /> Storico versioni ({versions.length})
                        </div>
                        {versions.length === 0 ? (
                            <div className="text-xs text-ink-500 italic">Nessuna versione precedente archiviata.</div>
                        ) : (
                            <ul className="space-y-2">
                                {versions.map((v) => (
                                    <li key={v.id} data-testid={`admin-legal-version-${v.id}`} className="flex items-center gap-3 border border-white/5 bg-ink-950/50 px-3 py-2">
                                        <div className="flex-1">
                                            <div className="text-sm text-ink-100">Versione {v.version} · <span className="text-ink-400">{v.format === "pdf" ? "PDF" : "Testo"}</span></div>
                                            <div className="text-[10px] text-ink-500">Pubblicata {fmtDate(v.published_at)} · sostituita {fmtDate(v.replaced_at)}</div>
                                        </div>
                                        <button
                                            onClick={() => setOpenVersion(v)}
                                            className="text-[10px] cta-outline px-2 py-1 flex items-center gap-1"
                                            data-testid={`admin-legal-version-view-${v.id}`}
                                        >
                                            <Eye size={11} /> Vedi
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>
            </div>
            {openVersion && (
                <VersionOverlay docType={docType} version={openVersion} onClose={() => setOpenVersion(null)} />
            )}
        </div>
    );
}

function VersionOverlay({ docType, version, onClose }) {
    const [detail, setDetail] = useState(null);
    useEffect(() => {
        api.get(`/admin/legal/documents/${docType}/versions/${version.id}`).then((r) => setDetail(r.data)).catch(() => setDetail({}));
    }, [docType, version.id]);
    return (
        <div className="fixed inset-0 z-[1200] flex items-center justify-center bg-black/85 backdrop-blur-sm p-4" onClick={onClose} data-testid={`admin-legal-version-overlay-${version.id}`}>
            <div className="relative w-full max-w-3xl max-h-[85vh] border border-gold/30 bg-ink-950 flex flex-col" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between border-b border-white/10 px-6 py-4">
                    <div className="font-serif text-lg text-ink-50">Versione {version.version} — {DOC_META[docType]?.title}</div>
                    <button onClick={onClose} data-testid={`admin-legal-version-close-${version.id}`} className="text-ink-400 hover:text-ink-50" aria-label="Chiudi versione"><X size={16} /></button>
                </div>
                <div className="flex-1 overflow-y-auto p-6">
                    {!detail ? <div className="text-ink-400">Caricamento...</div> : (
                        detail.format === "pdf" ? (
                            <div className="h-[65vh]">
                                <iframe title="version-pdf" src={`${process.env.REACT_APP_BACKEND_URL}/api/admin/legal/documents/${docType}/versions/${version.id}/pdf`} className="w-full h-full border border-white/10 bg-white" />
                            </div>
                        ) : (
                            <pre className="whitespace-pre-wrap font-sans text-[13px] leading-relaxed text-ink-100">{detail.content_text || "—"}</pre>
                        )
                    )}
                </div>
            </div>
        </div>
    );
}

// ============================================================
// Acceptances view (L3): per-agency status
// ============================================================
function AcceptancesView({ docType, onClose }) {
    const meta = DOC_META[docType];
    const Icon = meta.icon;
    const [data, setData] = useState(null);
    const [q, setQ] = useState("");
    const [status, setStatus] = useState("");
    const [df, setDf] = useState("");
    const [dt, setDt] = useState("");
    const [loadError, setLoadError] = useState("");

    const load = useCallback(async () => {
        setLoadError("");
        try {
            const params = {};
            if (q) params.q = q;
            if (status) params.status = status;
            if (df) params.date_from = df;
            if (dt) params.date_to = dt;
            const r = await api.get(`/admin/legal/acceptances/${docType}`, { params });
            setData(r.data);
        } catch (e) {
            setData({ items: [], counts: {} });
            setLoadError(e?.response?.data?.detail || "Errore nel caricamento delle accettazioni. Verifica i filtri e riprova.");
        }
    }, [docType, q, status, df, dt]);
    useEffect(() => { load(); }, [load]);

    const csvUrl = useMemo(() => {
        const p = new URLSearchParams();
        if (q) p.set("q", q);
        if (status) p.set("status", status);
        if (df) p.set("date_from", df);
        if (dt) p.set("date_to", dt);
        const qs = p.toString();
        return `${process.env.REACT_APP_BACKEND_URL}/api/admin/legal/acceptances/${docType}/export.csv${qs ? "?" + qs : ""}`;
    }, [docType, q, status, df, dt]);

    return (
        <div data-testid={`admin-legal-acceptances-${docType}`} className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" onClick={onClose}>
            <div className="relative w-full max-w-6xl max-h-[92vh] border border-gold/30 bg-ink-950 flex flex-col" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between border-b border-white/10 px-6 py-4">
                    <div>
                        <div className="font-serif text-lg text-ink-50 flex items-center gap-2">
                            <Icon size={16} className="text-gold" /> Accettazioni — {meta.title}
                        </div>
                        <div className="text-[10px] uppercase tracking-wider text-ink-400 mt-1">Versione corrente: {data?.current_version || "—"}</div>
                    </div>
                    <button onClick={onClose} data-testid={`admin-legal-acceptances-close-${docType}`} className="text-ink-400 hover:text-ink-50" aria-label="Chiudi accettazioni"><X size={16} /></button>
                </div>

                <div className="flex-1 overflow-y-auto p-6 space-y-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <MiniStat label="Agenzie totali" value={data?.counts?.total_agencies ?? 0} />
                        <MiniStat label="Versione corrente" value={data?.counts?.accepted_current ?? 0} accent="text-green-300" />
                        <MiniStat label="Versione precedente" value={data?.counts?.accepted_outdated ?? 0} accent="text-yellow-300" />
                        <MiniStat label="Mai accettato" value={data?.counts?.never_accepted ?? 0} accent="text-red-300" />
                    </div>

                    <div className="flex flex-wrap gap-2 items-center border border-white/10 bg-ink-900/40 p-3">
                        <div className="relative flex-1 min-w-[200px]">
                            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-500" />
                            <input
                                data-testid={`admin-legal-search-${docType}`}
                                placeholder="Cerca agenzia (email o nome)..."
                                value={q} onChange={(e) => setQ(e.target.value)}
                                className="w-full bg-transparent border border-white/15 pl-9 pr-3 py-2 text-sm text-ink-100 outline-none focus:border-gold"
                            />
                        </div>
                        <select data-testid={`admin-legal-filter-status-${docType}`} value={status} onChange={(e) => setStatus(e.target.value)} className="bg-ink-950 border border-white/15 px-3 py-2 text-sm text-ink-100 focus:border-gold outline-none">
                            <option value="">Tutti gli stati</option>
                            <option value="current">Versione corrente</option>
                            <option value="outdated">Versione precedente</option>
                            <option value="never">Mai accettato</option>
                        </select>
                        <input type="date" data-testid={`admin-legal-df-${docType}`} value={df} onChange={(e) => setDf(e.target.value)} className="bg-ink-950 border border-white/15 px-2 py-1.5 text-sm text-ink-100 focus:border-gold outline-none" />
                        <input type="date" data-testid={`admin-legal-dt-${docType}`} value={dt} onChange={(e) => setDt(e.target.value)} className="bg-ink-950 border border-white/15 px-2 py-1.5 text-sm text-ink-100 focus:border-gold outline-none" />
                        <button onClick={load} data-testid={`admin-legal-refresh-${docType}`} className="cta-outline px-3 py-2 text-[11px] flex items-center gap-1.5"><RefreshCw size={12} /> Aggiorna</button>
                        <a href={csvUrl} data-testid={`admin-legal-csv-${docType}`} className="cta-gold px-3 py-2 text-[11px] flex items-center gap-1.5"><Download size={12} /> CSV</a>
                    </div>

                    {loadError && (
                        <div data-testid={`admin-legal-acc-error-${docType}`} className="border border-red-700/40 bg-red-900/20 text-red-200 text-sm p-3">
                            {loadError}
                        </div>
                    )}

                    <div className="border border-white/10 overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-ink-950 text-[10px] uppercase tracking-wider text-ink-400">
                                <tr>
                                    <th className="px-3 py-2.5 text-left">Agenzia</th>
                                    <th className="px-3 py-2.5 text-left">Email</th>
                                    <th className="px-3 py-2.5 text-left">Stato</th>
                                    <th className="px-3 py-2.5 text-left">Versione accettata</th>
                                    <th className="px-3 py-2.5 text-left">Data e ora</th>
                                    <th className="px-3 py-2.5 text-left">IP</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data?.items?.length === 0 && (
                                    <tr><td colSpan={6} className="text-center text-ink-500 py-8 text-xs italic">Nessuna agenzia corrisponde ai filtri.</td></tr>
                                )}
                                {data?.items?.map((r) => {
                                    const sm = STATUS_META[r.status] || STATUS_META.never;
                                    const SIcon = sm.icon;
                                    return (
                                        <tr key={r.user_id} data-testid={`admin-legal-row-${docType}-${r.user_id}`} className="border-t border-white/5 hover:bg-white/5">
                                            <td className="px-3 py-2.5 text-ink-100">
                                                {r.agency_name}
                                                {r.is_office && <span className="ml-2 text-[9px] uppercase tracking-wider px-1.5 py-0.5 border border-white/10 text-ink-400">Ufficio</span>}
                                            </td>
                                            <td className="px-3 py-2.5 text-ink-300 text-[12px]">{r.email}</td>
                                            <td className="px-3 py-2.5">
                                                <span className={`text-[9px] uppercase tracking-wider px-2 py-0.5 border inline-flex items-center gap-1 ${sm.cls}`}>
                                                    <SIcon size={9} /> {sm.label}
                                                </span>
                                            </td>
                                            <td className="px-3 py-2.5 text-ink-100 font-mono text-[11px]">{r.accepted_version || "—"}</td>
                                            <td className="px-3 py-2.5 text-ink-400 text-[11px]">{r.accepted_at ? fmtDate(r.accepted_at) : "—"}</td>
                                            <td className="px-3 py-2.5 text-ink-400 font-mono text-[11px]">{r.ip || "—"}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
}

function MiniStat({ label, value, accent }) {
    return (
        <div className="border border-white/10 bg-ink-900/40 p-3">
            <div className={`text-[10px] uppercase tracking-wider ${accent || "text-ink-400"}`}>{label}</div>
            <div className="font-serif text-2xl text-ink-50 mt-1">{value}</div>
        </div>
    );
}
